import axios from 'axios';
import { readActiveProfile } from 'utils/activeProfile';
export const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001/api';

const instance = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    }
});

// A function that returns a fresh Auth0 access token. Registered at app startup
// by <AuthTokenBridge/> (which has access to the Auth0 React context). Kept in a
// module-level variable so this plain axios instance can reach it.
let tokenGetter = null;
export const registerTokenGetter = (fn) => {
    tokenGetter = fn;
};

// Same entry the /admin portal writes, so one admin login per tab covers both.
const ADMIN_KEY_STORAGE = 'sf_admin_key';
export const getAdminKey = () => {
    try { return sessionStorage.getItem(ADMIN_KEY_STORAGE) || ''; } catch { return ''; }
};
export const setAdminKey = (key) => {
    try { sessionStorage.setItem(ADMIN_KEY_STORAGE, key); } catch { /* storage blocked */ }
};
export const clearAdminKey = () => {
    try { sessionStorage.removeItem(ADMIN_KEY_STORAGE); } catch { /* storage blocked */ }
};

// Attach the Auth0 access token to every request so the backend can authenticate.
instance.interceptors.request.use(
    async (config) => {
        const adminKey = getAdminKey();
        if (adminKey) config.headers['X-Admin-Key'] = adminKey;
        if (tokenGetter) {
            try {
                const token = await tokenGetter();
                if (token) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
            } catch (e) {
                // No valid token — let the request proceed and be rejected (401),
                // which the response interceptor turns into a redirect to /login.
                console.warn('Could not attach access token:', e?.message || e);
            }
        }
        // Which of the email's profiles account-level routes act on.
        const profile = readActiveProfile();
        if (profile) config.headers['X-Profile-Token'] = profile.token;
        return config;
    },
    (error) => Promise.reject(error)
);

// Handle auth failures globally: send the user back to sign in.
instance.interceptors.response.use(
    (response) => response,
    (error) => {
        const status = error.response?.status;
        if (status === 401) {
            // Session expired or missing — restart the login flow.
            if (window.location.pathname !== '/login') {
                window.location.href = '/login';
            }
        }
        console.error('API Error:', error.response?.data || error.message);
        return Promise.reject(error);
    }
);

export default instance;
